<template>
    <div>
        <DataTable
            :data="revenueEntriesData"
            centered
            :columns="[
                {
                    title: 'Title',
                    field: 'title',
                },
                {
                    title: 'Customer',
                    field: 'customer',
                },
                {
                    title: 'Net Value',
                    field: 'value',
                },
                {
                    title: 'Pending',
                    field: 'pending',
                },

                {
                    title: '',
                    field: 'actions',
                    width: 60,
                },
            ]"
        >
            <template #column(title)="{ row }">
                {{ row.name }}
            </template>
            <template #column(value)="{ row }">
                {{ formatCurrency(row.subtotal, 2, row.target_currency, row.conversion_rate) }}
            </template>
            <template #column(pending)="{ row }">
                {{ formatCurrency(row.total_pending, 2, row.target_currency, row.conversion_rate) }}
            </template>
            <template #column(customer)="{ row }">
                {{ row.supplier.name }}
            </template>
            <template #column(actions)="{ row }">
                <Tooltip v-if="Auth.can('view purchase orders')" position="left" label="View">
                    <Button @click="openRE(row.id)"><i class="mdi mdi-eye"></i></Button>
                </Tooltip>
            </template>
        </DataTable>
        <Button v-if="!disabled" class="tw-w-full" @click="createRE()">
            <i class="mdi mdi-plus"></i>
            Create a Revenue Entry
        </Button>
    </div>
</template>
<script setup lang="ts">
import DataTable from '@/js/components/tables/DataTable.vue';
import Button from '@/js/components/controls/Button.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import { formatCurrency, Auth } from '@/js/utils.js';
import { ref, computed } from 'vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import { Eventbus } from '@/js/utils/eventbus.ts';
import type { BudgetEntry } from '@/js/types/budgetEntry.ts';
import type { RevenueEntry } from '@/js/types/revenueEntry.ts';
import type { Department } from '@/js/types/department.ts';

const props = defineProps<{
    entry: BudgetEntry;
    revenueEntries: RevenueEntry[];
    disabled: boolean;
}>();

const emit = defineEmits(['opened']);

const store = ref(useDataStore());
const revenueEntriesData = computed(() => {
    return props.revenueEntries.filter(
        (revenueEntry) =>
            revenueEntry.budget_entry_id === props.entry.id && revenueEntry.event_id === props.entry.event_id
    );
});

const openRE = (id: number) => {
    Eventbus.$emit('revenue:entry', {
        entry: id,
        source: {
            emitter: 'budget:entry',
            payload: {
                event: props.entry.event_id,
                entry: props.entry.id,
            },
        },
    });
    emit('opened');
};

const createRE = () => {
    const department = (Object.values(store.value.get('current_event.revenue_departments')) as Department[]).find(
        (revenueDepartment) => revenueDepartment.id === props.entry.category?.department_id
    );
    if (!Auth.can('create revenue entries') || !department) {
        return;
    }
    Eventbus.$emit('revenue:create', {
        department: department.id,
        budgetEntry: props.entry.id,
        category: props.entry.category?.id,
        supplier: props.entry.supplier?.id,
    });
};
</script>
